/**
 * Atlas flyout singleton — Vue 2.7 port of rm-portal-beta's atlasFlyout.ts.
 *
 * The flyout's chat state must survive route navigation — if we re-called
 * `agentChat()` on every mount, the user's conversation would reset every
 * time they opened the flyout on a different page. So we lazily build ONE
 * instance at module scope and hand it to anyone who asks.
 *
 * Independent of the full-page `/u/:user/agent` (`agentChat` route) chat:
 * they are two separate conversations. The flyout is the "quick ask while
 * I'm looking at X" surface; the full page is the deep-exploration
 * surface. Unifying them is possible later (share `conversationId` via the
 * Vuex store) — not done yet.
 *
 * Vue 2.7 differences from the Vue 3 source:
 *   - No `useRoute()` composable in vue-router 3.5.x. We read `$route`
 *     via `getCurrentInstance().proxy.$route` inside a `computed`, which
 *     stays reactive because Vue's reactivity system tracks `$route`
 *     access automatically (the router stores the current route on a
 *     `Vue.observable`-backed instance).
 *   - All type annotations dropped — plain JS.
 *   - Suppressed-route name is `'agentChat'` (rm-portal's full-page
 *     route name) rather than `'agent'` (rm-portal-beta's name).
 */
import {
    ref,
    shallowRef,
    computed,
    watch,
    nextTick,
    onBeforeUnmount,
    getCurrentInstance,
} from 'vue';
import { agentChat } from '@/composables/agent/agentChat';

let _chat = null;
let _unreadWatchWired = false;
let _pageActionWatchWired = false;
let _contextSwitchWatchWired = false;

/**
 * In-memory map of `contextKey → conversationId`. As the user navigates
 * between pages we stash the current conversation id under the OUTGOING
 * context's key and look up the INCOMING context's key. This is a
 * per-session cache only; on hard reload the map empties and each
 * context starts fresh (or resumes from server-side history via the
 * existing "previous conversations" panel).
 *
 * Why a Map keyed by stringified context rather than the server's
 * `agent_conversation.page_context_*` columns? The server only indexes
 * documents today, so a session-scoped map covers every page kind the
 * flyout cares about (grids, the alljobs/myjobs route split, etc.)
 * without waiting on backend schema changes.
 */
const _conversationsByContext = new Map();
let _lastContextKey = null;

/**
 * In-memory map of `contextKey → was Atlas open here?`. Pages remember
 * their own Atlas open state: opening the flyout (or a document's
 * inline Atlas tab) marks Atlas open for THAT context only. On every
 * context transition `setPageContext` stashes the live `_open` under
 * the outgoing key and restores the incoming context's remembered
 * state; a context the user never opened Atlas on restores to closed.
 *
 * This is what stops a document's Atlas tab leaking an open flyout
 * onto the page underneath when the document closes — and,
 * symmetrically, re-opening that document re-selects its Atlas tab.
 * Session-scoped, like `_conversationsByContext`.
 */
const _openByContext = new Map();

/**
 * Stable key derived from a pageContext object — equal keys mean
 * "same conversational scope". Two pages with the same kind+ref share
 * the same conversation; a transition between two unequal keys is what
 * triggers the switch/reset.
 *
 * Pages that don't set a context (or set one without a usable ref)
 * collapse to `'__default__'` so navigating between unrelated
 * context-less pages doesn't churn the chat — they stay in the
 * default conversation.
 */
function _contextKey(ctx) {
    if (!ctx || typeof ctx.kind !== 'string' || !ctx.kind) return '__default__';
    if (ctx.kind === 'document') {
        const ref =
            (ctx.data && (ctx.data.reference || ctx.data.doc_id)) || null;
        return ref ? `document:${ref}` : '__default__';
    }
    if (ctx.kind === 'documents.grid') {
        const route = (ctx.data && ctx.data.route) || 'grid';
        return `documents.grid:${route}`;
    }
    return `${ctx.kind}`;
}

/**
 * vue-router instance captured the first time any component calls
 * `useAtlasFlyout()`. The chat-side `lastPageAction` watcher runs at
 * module scope (no component instance) so it can't reach `$router` via
 * `getCurrentInstance()`. Storing the router here gives the dispatch
 * path a reactive source of "what route is the user on right now",
 * which is what scopes the `(route × kind)` preference.
 */
let _router = null;

/**
 * Returns the singleton chat instance. First call creates it; subsequent
 * calls return the same object so refs are shared across every mount.
 *
 * On first creation we also wire the unread-count watcher. It ignores
 * the initial message population (the /agent/init greeting that lands
 * while status is still 'initialising') so the user doesn't see a
 * phantom "1 unread" on cold load, and then counts any growth in the
 * messages array that happens while the flyout is closed.
 */
function getChat() {
    if (_chat === null) {
        _chat = agentChat();
    }
    if (!_unreadWatchWired) {
        _unreadWatchWired = true;
        const chat = _chat;
        watch(
            () => chat.messages.value.length,
            (newLen, oldLen) => {
                // Skip the initial population: any growth while the chat
                // is still initialising is the greeting + replayed
                // history, not something the user needs to be pulled
                // back to. Once `status` flips out of 'initialising',
                // subsequent growth-while-closed is the real unread signal.
                if (chat.status.value === 'initialising') return;
                const added = newLen - (oldLen ?? 0);
                if (added <= 0) return;
                if (_open.value) return;
                _unreadCount.value += added;
            },
        );
    }
    if (!_pageActionWatchWired) {
        _pageActionWatchWired = true;
        wirePageActionBridge(_chat);
    }
    if (!_contextSwitchWatchWired) {
        _contextSwitchWatchWired = true;
        const chat = _chat;
        // Per-context conversation scoping. The flyout used to share
        // one conversation across every route, which meant a chat
        // about Document A was still visible after navigating to
        // Document B (or to alljobs). We now keep an in-memory map of
        // `contextKey → conversationId` and swap the chat as the user
        // moves between pages: same context resumes the prior thread,
        // a new context starts fresh.
        //
        // Reconciliation runs on `nextTick` rather than synchronously
        // because `usePageContext()` clears the outgoing page's context
        // to `null` in onBeforeUnmount BEFORE the incoming page's
        // `setup()` writes the new value. Without coalescing that
        // would fire us twice per nav (once for the transient null,
        // once for the real new context) and cause a spurious reset
        // followed by a switch. By scheduling once and reading the
        // settled value at flush time, we observe only the real
        // transition.
        /**
         * A document Atlas just created is now the active context and its
         * thread is in place: open the panel and seed the note.
         *
         * This MUST run after the switch/reset below. Seeding from
         * `setPageContext` (where this started) pushed the note into the
         * OUTGOING thread, which the switch then replaced — the note
         * vanished and the panel showed the previous conversation.
         */
        const applyPendingCreation = () => {
            const pending = _pendingCreation.value;
            if (!pending) return;
            // NOTE: deliberately NOT gated on flyout visibility. The Atlas
            // surface inside a document is an INLINE host (the document's
            // own Atlas tab), which suppresses the slide-in — gating here
            // killed the greeting in exactly the place it is wanted.

            const ctx = chat.pageContext.value;
            if (!ctx || ctx.kind !== 'document') return;
            if (ctx.data?.reference !== pending.reference) return;
            _pendingCreation.value = null;
            // Set unconditionally. `open` is Atlas's "should be on screen"
            // state, not the slide-in's: the overlay renders on
            // `open && !globalHidden`, so an inline host still hides it,
            // while Document_Holder's `atlasOpen` watcher turns this into
            // "select the Atlas tab". Skipping it when an inline host was
            // registered is why the document opened with the tab dormant —
            // Document_Holder registers on mount, whether or not the tab
            // is the one showing.
            _open.value = true;
            if (pending.note) chat.noteAssistant(pending.note);
        };

        /** Is the just-created document the context we are landing on? */
        const pendingMatchesContext = () => {
            const pending = _pendingCreation.value;
            const ctx = chat.pageContext.value;
            return (
                !!pending &&
                ctx?.kind === 'document' &&
                ctx.data?.reference === pending.reference
            );
        };

        /**
         * A just-created document gets a CLEAN thread: the first thing the
         * user sees in the document is what Atlas made, not a replay of the
         * workflow that made it (dead step cards and all). That workflow
         * thread stays pinned under the context it ran in, so the surface
         * that opened the document still has it.
         */
        const startFreshForCreation = (contextKey) => {
            // reset() clears synchronously and mints the new id, then
            // initialises — so the note is seeded on settle. No greeting:
            // the user got here from an Atlas link and the note IS the
            // opening message, so the orientation blurb only pushes it down.
            const initialised = chat.reset({ skipGreeting: true });
            _conversationsByContext.set(contextKey, chat.conversationId.value);
            Promise.resolve(initialised)
                .catch(() => {})
                .finally(applyPendingCreation);
        };

        let reconcilePending = false;
        const reconcile = () => {
            reconcilePending = false;
            const nextKey = _contextKey(chat.pageContext.value);
            if (_lastContextKey === null) {
                // First observed context — seed without touching the
                // chat. The current conversation belongs to this
                // context by default.
                _lastContextKey = nextKey;
                if (pendingMatchesContext()) {
                    startFreshForCreation(nextKey);
                    return;
                }
                _conversationsByContext.set(
                    nextKey,
                    chat.conversationId.value,
                );
                return;
            }
            // Already on this context (the panel may simply have been
            // reopened, or the thread that CREATED the document is pinned
            // to this key). A pending creation still starts clean.
            if (nextKey === _lastContextKey) {
                if (pendingMatchesContext() && chat.status.value !== 'sending') {
                    startFreshForCreation(nextKey);
                }
                return;
            }
            // Mid-turn navigation: a reset/switch here would discard
            // in-flight work and confuse the user. Skip; the next nav
            // will reconcile.
            if (chat.status.value === 'sending') return;
            // Stash current conversation under the outgoing key so
            // coming back later resumes it.
            _conversationsByContext.set(
                _lastContextKey,
                chat.conversationId.value,
            );
            _lastContextKey = nextKey;
            if (pendingMatchesContext()) {
                startFreshForCreation(nextKey);
                return;
            }
            const saved = _conversationsByContext.get(nextKey);
            if (saved && saved !== chat.conversationId.value) {
                chat.switchConversation(saved)
                    .catch((err) => {
                        // eslint-disable-next-line no-console
                        console.error(
                            '[atlas] switch on context change failed; falling back to reset',
                            err,
                        );
                        chat.reset();
                    })
                    // The replayed history is in place — only now is the
                    // note landing in the right thread.
                    .finally(applyPendingCreation);
            } else if (!saved) {
                chat.reset();
                // `reset()` mints a new conversationId — pin it to
                // this context so navigating away and back resumes
                // here rather than minting again.
                _conversationsByContext.set(
                    nextKey,
                    chat.conversationId.value,
                );
                applyPendingCreation();
            } else {
                applyPendingCreation();
            }
        };
        watch(
            () => chat.pageContext.value,
            () => {
                if (reconcilePending) return;
                reconcilePending = true;
                nextTick(reconcile);
            },
        );
    }
    return _chat;
}

// Open/close state — module-level ref so the header toggle and the
// flyout's close button read/write the same value without prop drilling.
const _open = ref(false);

/**
 * Persisted flyout width. Lives at module scope so the App-shell can
 * reserve matching right-padding on `<v-main>` without prop drilling
 * through the flyout's internal resize state. localStorage-backed so
 * the user's choice survives reloads; clamped on read in case the
 * viewport shrunk between sessions.
 */
const FLYOUT_STORAGE_KEY = 'rm.atlas.flyoutWidth';
const FLYOUT_MIN = 360;
const FLYOUT_MAX_RATIO = 0.75;
const FLYOUT_DEFAULT_WIDTH = 420;

function clampFlyoutWidth(w) {
    const vw = (typeof window !== 'undefined' && window.innerWidth) || 1280;
    const maxByViewport = Math.floor(vw * FLYOUT_MAX_RATIO);
    const max = Math.max(FLYOUT_MIN, maxByViewport);
    return Math.min(Math.max(w, FLYOUT_MIN), max);
}

function loadStoredWidth() {
    try {
        const raw = localStorage.getItem(FLYOUT_STORAGE_KEY);
        const parsed = raw ? Number(raw) : NaN;
        if (!Number.isFinite(parsed)) return FLYOUT_DEFAULT_WIDTH;
        return clampFlyoutWidth(parsed);
    } catch {
        return FLYOUT_DEFAULT_WIDTH;
    }
}

const _flyoutWidth = ref(loadStoredWidth());

/**
 * Drag state. Hoisted alongside width so consumers (e.g. App.vue's
 * v-main padding) can suspend their CSS transitions during a drag —
 * otherwise the main content lags behind the flyout edge.
 */
const _resizing = ref(false);

function setFlyoutWidth(w, { persist = false } = {}) {
    _flyoutWidth.value = clampFlyoutWidth(w);
    if (persist) {
        try {
            localStorage.setItem(FLYOUT_STORAGE_KEY, String(_flyoutWidth.value));
        } catch {
            /* localStorage unavailable — persistence is best-effort */
        }
    }
}

function resetFlyoutWidth() {
    setFlyoutWidth(FLYOUT_DEFAULT_WIDTH, { persist: true });
}

function setResizing(val) {
    _resizing.value = val;
}

/**
 * Current page context, set by page-level hosts (document modal, skill
 * manager, hierarchy view, etc.) via `setPageContext`. The flyout
 * subscribes to this so the in-panel context chip and the next
 * `sendMessage` payload both reflect whatever page the user is on.
 */
const _pageContext = ref(null);

/**
 * Set while an *active* host deliberately holds the page context at null
 * — e.g. a document modal that's still mounted but whose Atlas
 * doc-editing toggle (`atlas_doc_editor`) is off. The `usePageContext`
 * null-restore watcher checks this so it doesn't immediately re-assert
 * the underlying page's context (which would make the "previous page
 * context" reappear the moment the overlay clears it). A plain null —
 * the unmount path — leaves this false, so the host still restores when
 * the overlay actually goes away.
 *
 * Reactive (a ref, not a plain bool) so the `usePageContext`
 * null-restore watcher can react to the hold being *released* even when
 * the context value itself stays null — closing an overlay that was
 * holding null is a null→null transition the value watcher alone would
 * miss, leaving the host page's context permanently cleared.
 */
const _holdNullContext = ref(false);
// True while a modal OVERLAY (the document view) owns the Atlas context
// rather than the routed page underneath. Overlays don't change the
// route, so anything keyed off `route.meta` — notably the context
// workflow auto-start — must consult this or it acts for the page behind
// the modal: opening Atlas inside a document used to start the parent
// page's guided workflow.
const _overlayOwnsContext = ref(false);
// A document Atlas just created, waiting for its page to open: when the
// document view declares its context we open the panel on that thread and
// seed the note. Set BEFORE the open is dispatched — matching on the
// reference is what keeps this to the one document, and doing it on the
// context arrival is what keeps it out of the previous thread.
const _pendingCreation = ref(null);

// A Create-dialog "Generate with Atlas" hand-off waiting for the main
// Atlas page: which workflow to start, the entry step to park on, and
// the dialog's entered values to pre-fill its form with. A ref so an
// ALREADY-MOUNTED Atlas page can watch it and reset into a fresh
// conversation (whose auto-start then consumes it); on navigation the
// page mounts fresh and its auto-start consumes it directly.
const _pendingWorkflowStart = ref(null);

/** Watch-only view of the stash for vAgentChat (consume via take…). */
export const pendingWorkflowStart = _pendingWorkflowStart;

/**
 * Stash a workflow start for the main Atlas page to pick up —
 * `{ workflow_key, entry_step_key?, initial_values? }`. The caller
 * navigates to the Atlas route after setting it (a no-op when already
 * there — the page's watcher reacts instead).
 */
export function setPendingWorkflowStart(payload) {
    _pendingWorkflowStart.value =
        payload && payload.workflow_key ? payload : null;
}

/** Consume the stashed workflow start (one-shot; null when none). */
export function takePendingWorkflowStart() {
    const pending = _pendingWorkflowStart.value;
    _pendingWorkflowStart.value = null;
    return pending;
}

/**
 * Unread-message counter. Increments every time the shared chat's
 * `messages` array grows while the flyout is closed — primarily fires
 * when a background prompt-chain narration lands via
 * `agent:background_message`, but any new assistant message while the
 * panel is hidden counts. Reset to 0 when the user opens the flyout.
 */
const _unreadCount = ref(0);

/**
 * How many host components are currently rendering Atlas inline
 * (e.g. Document_Holder's right-panel "Atlas" tab). When > 0 the
 * global slide-in flyout is hidden — the host owns the surface
 * instead — but `open()` / `toggle()` / AtlasFlyoutButton continue to
 * work, so the header button can still drive the open state.
 *
 * This is a counter rather than a boolean so concurrent hosts (e.g.
 * nested docs in side-by-side) don't fight each other on
 * register/unregister order — the last one out flips the flag.
 */
const _inlineHostCount = ref(0);

/**
 * Host-page convenience: declare that this component renders Atlas
 * inline. While at least one such host is mounted, the global
 * slide-in flyout is hidden — the host's inline surface owns
 * visibility. Auto-decrements on unmount.
 *
 * MUST be called from `setup()` — relies on `onBeforeUnmount` for
 * the cleanup hook.
 */
export function registerInlineHost() {
    _inlineHostCount.value += 1;
    onBeforeUnmount(() => {
        _inlineHostCount.value = Math.max(0, _inlineHostCount.value - 1);
    });
}

// ── Page actions ──────────────────────────────────────────
//
// Atlas can ask the host page to apply something to its current view —
// "filter alljobs by these doc_ids", "update part X of the document",
// etc. The agent attaches a `page_action` envelope to its response;
// this module routes it to a per-page handler registered via
// `usePageAction(kind, handler)`.
//
// Confirmation flow: by default we show a chip in the flyout asking
// "yes / no / always". Saying "always" stores a localStorage
// preference keyed by `(routeName × kind)` so future actions of the
// same kind on the same page auto-apply. The chip on auto-applied
// actions offers a "Stop auto-applying" link that clears the
// preference — undoing the consent without undoing the action itself.
//
// The `(route × kind)` scope is the safety surface: consenting to
// auto-apply a benign `filter` doesn't silently consent to a
// destructive `update` on the same page.

const ACTION_PREF_PREFIX = 'rm.atlas.pageAction';

/**
 * Page-action kinds whose registered handler renders its own visible
 * confirmation/edit UI. For these, the auto-apply chip is suppressed
 * — showing "Applied to current page" while the user is mid-review
 * in a wizard would be confusing.
 *
 * Treat this as a small whitelist: most kinds DO benefit from the
 * chip (it's the only feedback that anything happened), so only add
 * here when the handler is a substantial UI in its own right.
 */
const HANDLER_OWNS_UI_KINDS = new Set([
    'document.suggest-content',
    // The standard document view opening IS the visible outcome — an
    // "Applied to current page" chip under it would be noise.
    'document.open',
]);

/**
 * Per-route default behaviour. Routes listed here auto-apply the named
 * action kinds the first time they fire, without prompting the user —
 * effectively a pre-seeded "always" preference. The user can still
 * click "Stop auto-applying" on the resulting chip to opt back into
 * confirmation; that writes an explicit `'ask'` preference which
 * overrides the default on subsequent turns.
 *
 * Use the `'*'` key for kinds that should auto-apply on EVERY route —
 * appropriate for kinds whose handler owns its own confirmation UI,
 * so the inline chip would just be redundant noise.
 *
 * Today:
 *   - `* × document.suggest-content` — the handler renders a per-part
 *     review wizard in the right panel, which itself prompts the user
 *     to accept / edit / skip each suggestion. A chip flow on top of
 *     that would just delay the wizard appearing.
 *   - `* × document.open` — the agent's open_document tool only fires
 *     on an explicit user instruction ("open RM-x", "edit RM-x"), so
 *     consent is the chat message itself; a confirmation chip would
 *     make the user say "open it" twice.
 */
const DEFAULT_AUTO_APPLY_KINDS = {
    '*': ['document.suggest-content', 'document.open'],
};

/** Map<kind, Set<handler>>. Handlers are invoked in registration order. */
const _pageActionHandlers = new Map();

/**
 * Reactive mirror of which kinds have at least one handler registered.
 * Shape: `{ [kind]: registeredCount }`. Used by ChatMessage to hide
 * CTAs whose host-page hook isn't wired on the current route — a
 * Compare button that does nothing reads as broken, so we just don't
 * render it. Kept as a separate ref because the `Map` above isn't
 * reactive in Vue 2.7 (no Map proxy), and we want consumers to react
 * to register/unregister without subscribing to every handler call.
 */
const _pageActionKinds = ref({});

function _bumpKind(kind, delta) {
    const current = _pageActionKinds.value[kind] ?? 0;
    const next = current + delta;
    const replacement = { ..._pageActionKinds.value };
    if (next <= 0) delete replacement[kind];
    else replacement[kind] = next;
    _pageActionKinds.value = replacement;
}

/**
 * Currently-displayed action chip in the flyout, or null when none.
 * Shape: { id, kind, payload, description?, routeName, status, appliedAutomatically }
 *   status: 'pending' | 'applied' | 'dismissed'
 * Replaced wholesale when a new action arrives — only one chip at a
 * time. Most actions resolve quickly so queueing would just add UX
 * complexity for a case that doesn't really happen.
 */
const _activeAction = ref(null);

function _prefKey(routeName, kind) {
    return `${ACTION_PREF_PREFIX}.${routeName || '_'}.${kind}`;
}

function getActionPreference(routeName, kind) {
    try {
        return localStorage.getItem(_prefKey(routeName, kind));
    } catch {
        return null;
    }
}

function setActionPreference(routeName, kind, value) {
    try {
        localStorage.setItem(_prefKey(routeName, kind), value);
    } catch {
        /* localStorage unavailable — best-effort */
    }
}

/**
 * Effective preference for a route+kind, falling back to the route
 * default when no explicit preference has been stored. Used by the
 * incoming-action dispatcher to decide whether to auto-apply or
 * prompt; "Stop auto-applying" writes an explicit `'ask'` to override
 * the default, and "Always" writes `'always'` to make it persistent
 * even on routes without a default.
 */
function resolveActionPreference(routeName, kind) {
    const explicit = getActionPreference(routeName, kind);
    if (explicit !== null) return explicit;
    if (DEFAULT_AUTO_APPLY_KINDS[routeName]?.includes(kind)) return 'always';
    if (DEFAULT_AUTO_APPLY_KINDS['*']?.includes(kind)) return 'always';
    return null;
}

function _currentRouteName() {
    return _router?.currentRoute?.name ?? null;
}

function _invokeHandlers(kind, payload) {
    const set = _pageActionHandlers.get(kind);
    if (!set || set.size === 0) return false;
    for (const handler of set) {
        try {
            handler(payload);
        } catch (err) {
            // Don't let one handler take out the rest. Log and continue.
            // eslint-disable-next-line no-console
            console.error(`[atlas] page-action handler for "${kind}" threw`, err);
        }
    }
    return true;
}

/**
 * Process an incoming `page_action` envelope from the agent. Decides
 * whether to apply immediately (stored "always" preference) or surface
 * a confirmation chip.
 *
 * No-op when no handler is registered for the kind: the page can't act
 * on it, so prompting the user would be a dead end. We could log this
 * as a "developer missed wiring" warning, but agents sending actions
 * to pages that don't support them is also a legitimate case (the
 * model has wide latitude); silent skip is the kinder default.
 */
function _onIncomingAction(action) {
    if (!action || !action.kind) return;
    const routeName = _currentRouteName();
    const handlers = _pageActionHandlers.get(action.kind);
    // eslint-disable-next-line no-console
    console.log(
        `[atlas] _onIncomingAction kind=${action.kind} route=${routeName} ` +
            `registeredHandlers=${handlers?.size ?? 0}`,
    );
    if (!handlers || handlers.size === 0) {
        _activeAction.value = null;
        return;
    }
    const pref = resolveActionPreference(routeName, action.kind);
    const base = {
        id: _actionId(),
        kind: action.kind,
        payload: action.payload ?? null,
        description: typeof action.description === 'string' ? action.description : null,
        routeName,
    };
    if (pref === 'always') {
        _invokeHandlers(action.kind, base.payload);
        // For kinds whose handler renders its own visible UI (e.g.
        // `document.suggest-content` opens a per-part review wizard
        // in the right panel), the inline "Applied" chip would just
        // be confusing noise — the wizard itself is the surface the
        // user interacts with. Suppress the chip for those kinds;
        // everything else gets the normal lingering chip with the
        // "Stop auto-applying" affordance.
        if (HANDLER_OWNS_UI_KINDS.has(action.kind)) {
            _activeAction.value = null;
        } else {
            _activeAction.value = {
                ...base,
                status: 'applied',
                appliedAutomatically: true,
            };
        }
        return;
    }
    _activeAction.value = {
        ...base,
        status: 'pending',
        appliedAutomatically: false,
    };
}

/**
 * Chip click handler. Mutates `_activeAction` in place + persists
 * preference + invokes handlers as the response dictates.
 *
 *   'yes'           → apply once, leave preference untouched
 *   'no'            → dismiss without applying
 *   'always'        → apply + remember the preference for this route×kind
 *   'stop-auto'     → clear preference; leaves current chip's `applied` state alone
 *   'dismiss'       → close the chip (used after the user reads an applied result)
 */
function respondToAction(response) {
    const a = _activeAction.value;
    if (!a) return;
    switch (response) {
        case 'yes':
            _invokeHandlers(a.kind, a.payload);
            _activeAction.value = { ...a, status: 'applied' };
            break;
        case 'no':
            _activeAction.value = null;
            break;
        case 'always':
            setActionPreference(a.routeName, a.kind, 'always');
            _invokeHandlers(a.kind, a.payload);
            _activeAction.value = { ...a, status: 'applied', appliedAutomatically: true };
            break;
        case 'stop-auto':
            // Write an explicit `'ask'` rather than clearing — on routes
            // with a default-auto entry (e.g. alljobs × document.filter)
            // a cleared preference would immediately re-engage the
            // default on the next action.
            setActionPreference(a.routeName, a.kind, 'ask');
            _activeAction.value = { ...a, appliedAutomatically: false };
            break;
        case 'dismiss':
            _activeAction.value = null;
            break;
    }
}

let _actionIdSeq = 0;
function _actionId() {
    _actionIdSeq += 1;
    return `pa_${Date.now()}_${_actionIdSeq}`;
}

/**
 * Per-page UI controls that the flyout renders above its input bar.
 * Pages declare these via `usePageControl(definition)` to surface
 * page-scoped settings (e.g. "Override Filters" on alljobs) without
 * the flyout itself needing to know what page it's on.
 *
 * Backed by `shallowRef`, NOT `ref`. The list contains entries whose
 * `modelValue` is itself a `Ref` — and Vue's normal reactive wrapping
 * auto-unwraps nested refs on access, so a deep `ref([])` would hand
 * the template a plain boolean and break `control.modelValue.value =
 * ...` assignments. `shallowRef` makes only the array reassignment
 * reactive; refs inside the entries survive intact.
 *
 * Entry shape:
 *   {
 *     id: string,             // auto-generated, used as :key
 *     kind: 'toggle' | 'button',
 *     label: string,          // visible chip/switch label
 *     description?: string,   // tooltip / a11y hint
 *     modelValue?: Ref,       // for 'toggle' — the page's reactive bool
 *     onClick?: () => void,   // for 'button'
 *     onOpen?: () => void,    // invoked when the flyout opens — see usePageControl
 *     onReset?: () => void,   // invoked when the flyout closes — see usePageControl
 *     contextKind?: string,   // match a whole context kind (e.g. 'documents.grid')
 *                             // instead of the exact mount-time key — see _isControlActive
 *     visible?: Ref|() => bool,// optional reactive gate; hidden while falsy — see _isControlVisible
 *   }
 */
const _pageControls = shallowRef([]);
let _pageControlIdSeq = 0;

/**
 * Host-page convenience: declare a control (toggle, button) that the
 * AtlasFlyout will render above its input bar while this page is
 * mounted. The definition is registered on call and auto-removed when
 * the calling component unmounts, so navigation cleanly swaps which
 * controls are visible.
 *
 *   const overrideFilters = ref(false);
 *   usePageControl({
 *       kind: 'toggle',
 *       label: 'Override Filters',
 *       description: 'Use docs from the chat to populate the grid',
 *       modelValue: overrideFilters,
 *   });
 *
 * Lifecycle hooks bridge the flyout's open state with the page's local
 * state — Atlas treats a registered control as live ONLY while the
 * flyout is open, so the page can opt in to per-open and per-close
 * behaviour:
 *
 *   - `onOpen()`  fires when the flyout opens (and immediately on
 *                 registration if the flyout is already open). Use it
 *                 to rehydrate the control from persistence so closing
 *                 + reopening the flyout reapplies the user's stored
 *                 preference.
 *   - `onReset()` fires when the flyout closes. Use it to clear
 *                 in-memory state without touching persistence —
 *                 controls that save user_view_settings should
 *                 suppress their save watcher across this call.
 *
 * Toggle controls that omit `onReset` fall back to restoring
 * `modelValue` to whatever it held at registration.
 *
 * MUST be called from `setup()` — relies on `onBeforeUnmount` for the
 * cleanup hook.
 */
export function usePageControl(definition) {
    if (!definition || typeof definition.kind !== 'string') return;
    _pageControlIdSeq += 1;
    const id = `pc_${_pageControlIdSeq}`;
    const initialModelValue =
        definition.kind === 'toggle' && definition.modelValue
            ? definition.modelValue.value
            : undefined;
    // Capture the page context that was active when this control was
    // registered. Controls only render while THAT context is still the
    // active one — opening a doc modal on top of an alljobs page swaps
    // the context to `document:…` and would otherwise leave alljobs's
    // "Override Filters" toggle visible even though the chat is now
    // scoped to the document, and toggling it would do nothing.
    const contextKey = _contextKey(_pageContext.value);
    const entry = { ...definition, id, initialModelValue, contextKey };
    _pageControls.value = [..._pageControls.value, entry];
    // Hot-mount: if the flyout is already open when the page registers,
    // fire onOpen so the control matches the active state without
    // waiting for the next open transition.
    if (_open.value) _runControlOpen(entry);
    onBeforeUnmount(() => {
        _pageControls.value = _pageControls.value.filter((c) => c.id !== id);
    });
    return { id };
}

/**
 * A control is "active" while the page context that was current when
 * the page registered it is still the active context. Controls
 * registered with no context (contextKey === '__default__') count as
 * active under the same default — they were declared by a page that
 * doesn't scope itself.
 */
/** Kind portion of a context key — `documents.grid:alljobs` → `documents.grid`. */
function _kindFromKey(key) {
    if (!key || typeof key !== 'string') return null;
    const i = key.indexOf(':');
    return i === -1 ? key : key.slice(0, i);
}

function _isControlActive(control, contextKey) {
    // A control may opt into matching a whole context KIND (e.g.
    // `documents.grid`) rather than one exact key. This lets a single
    // registration stay visible across sibling routes that share a kind but
    // differ by ref (alljobs vs myjobs → `documents.grid:alljobs` /
    // `:myjobs`) — the grid component doesn't remount between them, so a
    // key-exact match would otherwise pin the control to the mount-time route.
    if (control.contextKind) {
        return _kindFromKey(contextKey) === control.contextKind;
    }
    return control.contextKey === contextKey;
}

/**
 * Reactive view of "controls visible right now". The flyout's template
 * iterates this rather than `_pageControls` so an alljobs control
 * disappears the instant a doc modal pushes a new context — and
 * reappears when the alljobs context becomes active again.
 */
/**
 * Optional reactive visibility gate on a control. `visible` may be a getter
 * function or a ref; omitted → always visible (subject to context). Lets a
 * page hide a control until its data is ready — e.g. "Compare levels" only
 * once Atlas has surfaced documents into the chat. Read inside the
 * `_activePageControls` computed so the dependency is tracked and the control
 * shows/hides reactively.
 */
function _isControlVisible(control) {
    const v = control.visible;
    if (v === undefined || v === null) return true;
    if (typeof v === 'function') return !!v();
    if (v && typeof v === 'object' && 'value' in v) return !!v.value;
    return !!v;
}

const _activePageControls = computed(() => {
    const key = _contextKey(_pageContext.value);
    return _pageControls.value.filter(
        (c) => _isControlActive(c, key) && _isControlVisible(c),
    );
});

function _runControlOpen(control) {
    if (typeof control.onOpen !== 'function') return;
    try {
        control.onOpen();
    } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[atlas] page-control onOpen threw', err);
    }
}

function _runControlReset(control) {
    if (typeof control.onReset === 'function') {
        try {
            control.onReset();
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('[atlas] page-control onReset threw', err);
        }
        return;
    }
    if (
        control.kind === 'toggle' &&
        control.modelValue &&
        control.initialModelValue !== undefined &&
        control.modelValue.value !== control.initialModelValue
    ) {
        control.modelValue.value = control.initialModelValue;
    }
}

/**
 * Fire onOpen for every currently-active control. Invoked when the
 * flyout transitions from closed to open so persisted preferences
 * (e.g. Override Filters) can be rehydrated — closing the flyout
 * clears them in-memory, so reopening needs to ask the page to
 * reapply. Inactive controls (their owning context is no longer
 * active) are skipped — they were already reset at the context
 * boundary and shouldn't re-engage just because the flyout opened.
 */
function _activatePageControls() {
    const key = _contextKey(_pageContext.value);
    for (const control of _pageControls.value) {
        if (!_isControlActive(control, key)) continue;
        _runControlOpen(control);
    }
}

/**
 * Reset every active page control to its initial state. Invoked when
 * the flyout closes so Atlas-driven page state (e.g. Override
 * Filters) doesn't linger while the user is no longer engaging with
 * the agent. Inactive controls were already reset when their owning
 * context lost active status, so we skip them here.
 */
function _resetPageControls() {
    const key = _contextKey(_pageContext.value);
    for (const control of _pageControls.value) {
        if (!_isControlActive(control, key)) continue;
        _runControlReset(control);
    }
}

/**
 * Fire reset/open hooks for controls that just transitioned across
 * the active boundary because the flyout's page context changed.
 * Called from `setPageContext` (the only mutation point for
 * `_pageContext`) when the derived key actually moved — equal keys
 * are a no-op, so context updates that don't change identity (same
 * doc viewed twice in a row) won't churn control state.
 *
 * Reset always fires on inactive→…transitions (whether or not the
 * flyout is open) because the control's owning page is no longer the
 * conversational scope; leaving e.g. Override Filters engaged after
 * the user navigated into a single doc would mean the next alljobs
 * visit silently re-applies a stale filter. Open only fires when the
 * flyout is currently visible, mirroring the activate-on-open
 * lifecycle.
 */
function _reconcileActiveControls(oldKey, newKey) {
    for (const control of _pageControls.value) {
        const wasActive = _isControlActive(control, oldKey);
        const isActive = _isControlActive(control, newKey);
        if (wasActive && !isActive) {
            _runControlReset(control);
        } else if (!wasActive && isActive && _open.value) {
            _runControlOpen(control);
        }
    }
}

/**
 * Arbitrary cleanup handlers fired when the flyout closes. Used for
 * page state that's mutated outside the `usePageControl` lifecycle —
 * e.g. an Atlas-driven `docIdsIncluded` filter applied via a page
 * action has nowhere to hang its reset in `usePageControl`, so the
 * page registers a `usePageReset` handler instead.
 */
const _pageResetHandlers = new Set();

/**
 * Host-page convenience: register a handler that fires every time the
 * flyout closes, and auto-unregister when the calling component
 * unmounts. Use for component state mutated by page actions (or other
 * Atlas-driven side effects) that should disengage when the user
 * dismisses the agent.
 *
 *   usePageReset(() => {
 *       instance?.proxy?.clearAtlasFilter?.();
 *   });
 *
 * MUST be called from `setup()` — relies on `onBeforeUnmount` for the
 * cleanup hook. Handlers run after `usePageControl` resets so they can
 * observe the post-reset toggle state if needed.
 */
export function usePageReset(handler) {
    if (typeof handler !== 'function') return;
    _pageResetHandlers.add(handler);
    onBeforeUnmount(() => {
        _pageResetHandlers.delete(handler);
    });
}

function _firePageResetHandlers() {
    for (const handler of _pageResetHandlers) {
        try {
            handler();
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('[atlas] page-reset handler threw', err);
        }
    }
}

/**
 * Host-page convenience: register a handler for a page-action kind, and
 * auto-unregister when the calling component unmounts. The handler
 * receives the action's `payload` — whatever the agent decided to send.
 *
 *   usePageAction('document.filter', ({ docIds }) => {
 *       applyDocIdFilter(docIds);
 *   });
 *
 * Multiple components can register for the same kind on the same
 * route — all handlers fire. In practice the route's primary component
 * is the only one that registers; the multi-handler shape just keeps
 * the door open for embedded panels to participate later.
 */
export function usePageAction(kind, handler) {
    if (!kind || typeof handler !== 'function') return;
    let set = _pageActionHandlers.get(kind);
    if (!set) {
        set = new Set();
        _pageActionHandlers.set(kind, set);
    }
    set.add(handler);
    _bumpKind(kind, 1);
    // eslint-disable-next-line no-console
    console.log(
        `[atlas] usePageAction registered kind=${kind} total=${set.size}`,
    );
    onBeforeUnmount(() => {
        set.delete(handler);
        _bumpKind(kind, -1);
        // eslint-disable-next-line no-console
        console.log(
            `[atlas] usePageAction unregistered kind=${kind} remaining=${set.size}`,
        );
        if (set.size === 0) _pageActionHandlers.delete(kind);
    });
}

/**
 * Fire registered handlers for `kind` directly, bypassing the
 * confirmation chip. Used by AtlasFlyout to forward user-initiated
 * chat-UI actions (e.g. "Compare these documents") to the host page —
 * the user already clicked, so consent is implicit; the chip flow is
 * reserved for actions the AGENT decides to take on the page.
 *
 * Returns `true` when at least one handler ran, `false` when nothing
 * was registered. Callers can use the return to gate follow-up
 * behaviour (e.g. closing the flyout only when the dispatch landed).
 */
/**
 * Bridge agent → host page for ONE chat instance. The shared chat
 * composable surfaces each turn's `page_action` envelope on
 * `lastPageAction`; this routes it through the local registry /
 * preference / chip pipeline. Nulls are emitted on every turn that
 * doesn't carry one — ignored rather than dismissing the active chip,
 * so a previously auto-applied result stays visible (with its "Stop
 * auto-applying" link) until the next REAL action lands.
 *
 * The flyout singleton wires this automatically for its own chat, but
 * vAgentChat runs a SEPARATE chat instance — it must wire the bridge
 * itself in setup(), or agent-emitted actions (open_document) silently
 * no-op on the full-page chat (user report, 2026-09-03). Safe to call
 * from a component: the watcher is disposed with the caller's scope.
 */
export function wirePageActionBridge(chat) {
    watch(
        () => chat.lastPageAction.value,
        (action) => {
            if (!action) return;
            // eslint-disable-next-line no-console
            console.log('[atlas] lastPageAction received:', action);
            _onIncomingAction(action);
        },
    );
}

export function dispatchPageAction(kind, payload) {
    return _invokeHandlers(kind, payload);
}

/**
 * Per-conversation scroll position memory for the flyout's message
 * scroller. Kept at module scope so it survives the
 * mount/unmount cycles that happen when the user opens a document
 * modal (global flyout unmounts, inline flyout mounts; reverse on
 * close). Without this the scroller would reset to the top on every
 * remount even though the user's intent was to come back to where
 * they were in the same conversation.
 *
 * Stores `{ scrollTop, wasAtBottom }`. `wasAtBottom` is the signal
 * we'd otherwise lose to numeric drift — if the conversation grew
 * while away, the OLD `scrollTop` no longer points at the bottom, so
 * we scroll to the live `scrollHeight` instead when the user was
 * stuck there.
 */
const _scrollPositions = new Map();

export function saveScrollPosition(convId, payload) {
    if (!convId || !payload) return;
    _scrollPositions.set(convId, payload);
}

export function getScrollPosition(convId) {
    return convId ? _scrollPositions.get(convId) : undefined;
}

/**
 * Dev helper: expose the page-action dispatcher on the window so the
 * inline review wizard (or any future host UI) can be exercised from
 * the browser console without needing the agent backend deployed.
 *
 * Example:
 *   window.__atlasDispatchPageAction('document.suggest-content', {
 *     document_id: 123,
 *     suggestions: [{ doc_part_id: 456, part_label: 'Responsibilities',
 *       current_content: '', items: [{id:'i-1', text:'Lead the team'}] }],
 *   });
 *
 * No-op in production builds so we don't leak debug hooks to users.
 */
if (
    typeof window !== 'undefined' &&
    process.env.NODE_ENV !== 'production'
) {
    window.__atlasDispatchPageAction = dispatchPageAction;
}

/**
 * Reactive "is a handler registered for this kind on the current route".
 * Returns a `computed` so templates can `v-if` on it directly and pick
 * up registration changes (route navigation, dynamic mount/unmount).
 * ChatMessage uses this to hide CTAs whose host hook isn't wired.
 */
export function hasPageAction(kind) {
    return computed(() => (_pageActionKinds.value[kind] ?? 0) > 0);
}

/**
 * Public hook. Call from the header (to toggle), from the flyout (to
 * render), or from a page (to declare its context). Always returns the
 * same refs + the singleton chat instance.
 *
 * MUST be called from a `setup()` context — relies on
 * `getCurrentInstance()` to access the route reactively.
 */
/**
 * Close the flyout from OUTSIDE a component, without constructing the chat
 * singleton — the module-scope half of the hook's `close()`, which delegates here.
 *
 * Exists for the app shell: App.vue deliberately uses `useAtlasFlyoutLayout()` so
 * `/agent/init` can't race the axios baseURL interceptor (see that hook's note),
 * and it still has to shut the panel when a route takes over the whole viewport.
 */
export function closeAtlasFlyout() {
    const wasOpen = _open.value;
    _open.value = false;
    if (wasOpen) {
        _resetPageControls();
        _firePageResetHandlers();
    }
}

export function useAtlasFlyout() {
    const chat = getChat();

    // Vue 2.7 + vue-router 3.5 don't expose a `useRoute()` composable.
    // Read $route via the current instance proxy; wrapping in a
    // `computed` keeps reactivity — the router-side $route assignment
    // triggers Vue's dependency tracker.
    const instance = getCurrentInstance();
    const route = computed(() => instance?.proxy?.$route ?? null);

    // Capture the router for the module-scope page-action watcher (which
    // can't read $router itself — it runs outside any component). First
    // call wins; the router is a singleton so this is safe.
    if (!_router && instance?.proxy?.$router) {
        _router = instance.proxy.$router;
    }

    // Keep the chat's provenance route in step with the SPA — sent on
    // every turn so guided-workflow instances can record which page
    // they were actioned from. Multiple callers re-set the same value;
    // harmless and keeps the wiring local to route reactivity.
    watch(
        () => route.value?.name ?? null,
        (name) => {
            chat.routeName.value = name;
        },
        { immediate: true },
    );

    // Don't show the flyout on the full-page `/agent` route — the user
    // already has the whole Atlas surface, a second floating Atlas
    // would be confusing.
    const suppressed = computed(() => route.value?.name === 'agentChat');

    /**
     * Hide the global slide-in flyout (but keep open/toggle/button
     * interactive). True when:
     *   - the agentChat full page owns the Atlas surface (`suppressed`), or
     *   - any host page is rendering an inline Atlas panel.
     * Components gating the global flyout's visibility (App.vue's main
     * padding, AtlasFlyout's own `v-if`) should read this rather than
     * `suppressed`, so the inline-host case hides the slide-in without
     * also disabling the open() flow.
     */
    const globalHidden = computed(
        () => suppressed.value || _inlineHostCount.value > 0,
    );

    function open() {
        if (suppressed.value) return;
        const wasOpen = _open.value;
        _open.value = true;
        // Opening IS the "I've seen it" signal. Clear the badge
        // immediately rather than waiting for the user to scroll
        // through new content.
        _unreadCount.value = 0;
        if (!wasOpen) _activatePageControls();
    }
    function close() {
        closeAtlasFlyout();
    }
    function toggle() {
        if (suppressed.value) return;
        const next = !_open.value;
        _open.value = next;
        if (next) {
            _unreadCount.value = 0;
            _activatePageControls();
        } else {
            _resetPageControls();
            _firePageResetHandlers();
        }
    }

    /**
     * Set the current page context. Passing null clears it. The chat
     * instance reads `pageContext` on every sendMessage, so setting
     * here propagates to the next turn without restarting the
     * conversation.
     */
    /**
     * Remember that `reference` was just created by Atlas, with the note
     * to greet the user with when its document opens. Cleared once used,
     * or when another document is created first.
     */
    function setPendingCreation(reference, note) {
        _pendingCreation.value = reference ? { reference, note: note ?? '' } : null;
    }

    function setPageContext(ctx, { hold = false, overlay = false } = {}) {
        const oldKey = _contextKey(_pageContext.value);
        // An overlay either declares its own context or holds the page's
        // clear; a page setting its own context releases the ownership.
        _overlayOwnsContext.value = overlay || (ctx === null && hold);
        // Remember whether this null is an active overlay holding the
        // context clear (hold) vs. a clear-on-unmount (no hold). The
        // null-restore watcher in `usePageContext` reads this to decide
        // whether to re-assert the underlying page's context. Any
        // non-null context releases the hold.
        _holdNullContext.value = ctx === null && hold;
        _pageContext.value = ctx;
        chat.pageContext.value = ctx;
        const newKey = _contextKey(ctx);
        if (oldKey !== newKey) {
            // Per-context open memory: stash the outgoing page's open
            // state and restore the incoming page's — closed for a
            // context Atlas was never opened on. Written directly to
            // `_open` rather than via open()/close(): this is a restore,
            // not a user gesture, so the gesture side effects (unread
            // clear, page-control resets, page-reset handlers) must not
            // fire — an alljobs Atlas filter should survive a document
            // modal opening on top, exactly as it did when the flyout
            // stayed open across contexts.
            //
            // Runs BEFORE _reconcileActiveControls so controls crossing
            // into the new context see the restored open state (their
            // onOpen hooks only fire while the flyout is open there).
            _openByContext.set(oldKey, _open.value);
            _open.value = _openByContext.get(newKey) ?? false;
            _reconcileActiveControls(oldKey, newKey);
        }
    }

    return {
        chat,
        open: _open,
        suppressed,
        globalHidden,
        pageContext: _pageContext,
        // Read-only: is a modal overlay (not the routed page) the owner of
        // the current context? Route-keyed behaviour must respect this.
        overlayOwnsContext: _overlayOwnsContext,
        setPendingCreation,
        unreadCount: _unreadCount,
        setOpen: open,
        close,
        toggle,
        setPageContext,
        // Flyout width / resize state — shared so the app shell can
        // reserve matching space and suspend its transition during drag.
        flyoutWidth: _flyoutWidth,
        resizing: _resizing,
        setFlyoutWidth,
        resetFlyoutWidth,
        setResizing,
        // Page actions — chip state + responder. Host pages register
        // their handlers via the standalone `usePageAction()` export.
        activeAction: _activeAction,
        respondToAction,
        // Page controls — toggles/buttons the active page wants the
        // flyout to render above its input bar. Hosts add entries via
        // the standalone `usePageControl()` export. Exposes the
        // *active* subset (controls whose registering context is the
        // current flyout context) so an alljobs control disappears
        // the moment a doc modal pushes a new context on top.
        pageControls: _activePageControls,
    };
}

/**
 * Layout-only hook for the app shell. Returns the refs needed to
 * reserve right-padding on `<v-main>` (open / suppressed / flyoutWidth
 * / resizing) WITHOUT triggering the singleton chat instance.
 *
 * This split exists because `useAtlasFlyout()` calls `getChat()`,
 * which constructs the shared agentChat composable, which fires
 * `/agent/init` immediately. If App.vue's `setup()` called the full
 * hook, init would race ahead of the axios baseURL interceptor that
 * App.vue's `created()` registers — kicking the request at the Vue
 * dev server instead of the API. (The AtlasFlyout component itself
 * doesn't have this problem: it only mounts after authentication,
 * by which time `created()` has run.)
 *
 * Also captures the router on first call so the page-action
 * dispatcher can read the current route name without a component
 * instance.
 */
export function useAtlasFlyoutLayout() {
    const instance = getCurrentInstance();
    if (!_router && instance?.proxy?.$router) {
        _router = instance.proxy.$router;
    }
    const route = computed(() => instance?.proxy?.$route ?? null);
    const suppressed = computed(() => route.value?.name === 'agentChat');
    const globalHidden = computed(
        () => suppressed.value || _inlineHostCount.value > 0,
    );
    return {
        open: _open,
        suppressed,
        globalHidden,
        flyoutWidth: _flyoutWidth,
        resizing: _resizing,
    };
}

/**
 * Host-page convenience: declare the current page's Atlas context from a
 * reactive source, and clear it automatically when the component
 * unmounts or the source becomes null. Pass a getter (e.g. a computed)
 * that returns the context object — null/undefined returns clear it.
 *
 *   usePageContext(() => doc.value
 *       ? { kind: 'document', summary: `Viewing ${doc.value.reference}`, data: { … } }
 *       : null,
 *   );
 *
 * Intentionally simple: no patch/merge semantics. The caller owns the
 * whole context object. Pages don't need to coordinate with each
 * other — each navigation triggers `onBeforeUnmount` → clear, then the
 * next page's source fires → set.
 *
 * Modal-overlay restore: if a modal-mounted component (e.g.
 * cDocumentFullNEW) clears the context via `setPageContext(null)` on
 * its own unmount, the host page underneath would otherwise be stuck
 * with a null context — its `source` getter only re-runs when its
 * reactive deps change, and the route name hasn't moved. A secondary
 * watch on the global pageContext catches the null transition and
 * re-applies whatever the host's source currently evaluates to.
 */
export function usePageContext(source) {
    const { setPageContext, pageContext } = useAtlasFlyout();
    watch(source, (ctx) => setPageContext(ctx ?? null), { immediate: true });
    // Watch both the context value AND the hold flag: releasing a hold is
    // a null→null transition (the overlay was holding null, now it's
    // closing) that the value alone wouldn't surface, but it's exactly
    // when the host page needs to restore its context.
    watch([pageContext, _holdNullContext], ([ctx, held]) => {
        if (ctx !== null) return;
        // An active overlay is deliberately holding the context clear
        // (e.g. a document modal with Atlas doc-editing toggled off).
        // Restoring our page context here would defeat that — leave it.
        if (held) return;
        let ours = null;
        try {
            ours = source();
        } catch {
            ours = null;
        }
        if (ours) setPageContext(ours);
    });
    onBeforeUnmount(() => setPageContext(null));
}
